import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import FreshnessBadge from './components/FreshnessBadge';
import AdminIntegrationsSubnav from './components/AdminIntegrationsSubnav';
import { fetchAdminIntegration } from '../../../services/Http/AdminApi';

const AdminIntegrationDetail = () => {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchStatus, setFetchStatus] = useState('running');
  const loadRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setFetchStatus('running');
    setLoading(true);
    try {
      const resp = await fetchAdminIntegration(id);
      if (request !== loadRequest.current) return;
      setRecord(resp?.data || null);
      setFetchStatus('success');
    } catch (error) {
      if (request !== loadRequest.current) return;
      setFetchStatus('failed');
      toast.error(error?.response?.data?.message || 'Failed to load integration detail');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); return () => { loadRequest.current += 1; }; }, [load]);

  return (
    <PageContainer>
      <PageHeader><PageTitle>Integration Detail</PageTitle></PageHeader>
      <PageContent>
        <AdminIntegrationsSubnav />
        <Toolbar>
          <FreshnessBadge status={fetchStatus} sourceType="operational" />
          <ToolbarRight>
            <Link to="/integrations/list">Back to list</Link>
            <MainButton size="small" onClick={load}>Refresh</MainButton>
          </ToolbarRight>
        </Toolbar>

        {loading ? <Panel>Loading...</Panel> : fetchStatus === 'failed' ? <Panel role="alert">Failed to load integration detail</Panel> : !record ? <Panel>Integration not found</Panel> : (
          <Grid>
            <Card>
              <h3>Overview</h3>
              <KeyValue><span>ID</span><code>{record.id}</code></KeyValue>
              <KeyValue><span>Name</span><strong>{record.name || '—'}</strong></KeyValue>
              <KeyValue><span>Type</span><span>{record.integration_type || '—'}</span></KeyValue>
              <KeyValue><span>Organization</span><span>{record.organization_name || '—'}</span></KeyValue>
              <KeyValue><span>Venue</span><span>{record.venue_name || '—'}</span></KeyValue>
              <KeyValue><span>Status</span><span>{record.status || '—'}</span></KeyValue>
              <KeyValue><span>Webhook Health</span><span>{record.webhook_health || '—'}</span></KeyValue>
              <KeyValue><span>Active</span><span>{record.is_active ? 'Yes' : 'No'}</span></KeyValue>
              <KeyValue><span>Updated</span><span>{record.updated_at ? new Date(record.updated_at).toLocaleString() : '—'}</span></KeyValue>
            </Card>

            <Card>
              <h3>Connection</h3>
              <KeyValue><span>API URL</span><CodeWrap>{record.api_url || '—'}</CodeWrap></KeyValue>
              <KeyValue><span>Webhook URL</span><CodeWrap>{record.webhook_url || '—'}</CodeWrap></KeyValue>
              <KeyValue><span>Last Webhook Status</span><span>{record.last_webhook_status || '—'}</span></KeyValue>
              <KeyValue><span>Last Webhook Ping</span><span>{record.last_webhook_ping_at ? new Date(record.last_webhook_ping_at).toLocaleString() : '—'}</span></KeyValue>
              <KeyValue><span>Last Sync</span><span>{record.last_sync_at ? new Date(record.last_sync_at).toLocaleString() : '—'}</span></KeyValue>
              <Notice>
                Secrets and credentials are redacted by backend by default. This view intentionally does not expose them.
              </Notice>
            </Card>

            <Card $full>
              <h3>Config (Redacted / Lightweight)</h3>
              <JsonPanel>{pretty(record.config)}</JsonPanel>
            </Card>

            <Card $full>
              <h3>Field Mappings</h3>
              <JsonPanel>{pretty(record.field_mappings)}</JsonPanel>
            </Card>
          </Grid>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AdminIntegrationDetail;

const pretty = (value) => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const Toolbar = styled.div`display:flex;justify-content:space-between;gap: 0.75rem;align-items:center;margin-bottom: 1rem;flex-wrap:wrap;`;
const ToolbarRight = styled.div`display:flex;align-items:center;gap: 0.75rem; a { color: var(--color-primary); text-decoration:none; font-weight:600; }`;
const Panel = styled.div`border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};border-radius:12px;padding: 1rem;`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap: 1rem; @media (max-width: 900px){grid-template-columns:1fr;}`;
const Card = styled.div`
  border:1px solid ${({theme})=>theme.colors.border};
  background:${({theme})=>theme.colors.surface};
  border-radius:12px;
  padding: 1rem;
  ${({ $full }) => $full ? 'grid-column: 1 / -1;' : ''}
  h3 { margin: 0 0 0.75rem; color:${({theme})=>theme.colors.textPrimary}; }
`;
const KeyValue = styled.div`
  display:grid; grid-template-columns: 160px 1fr; gap: 0.625rem; padding: 0.5rem 0; border-bottom:1px solid ${({theme})=>theme.colors.border};
  &:last-child { border-bottom:none; }
  span:first-child { color:${({theme})=>theme.colors.textMuted}; font-weight:600; }
  code { font-size: 0.75rem; }
`;
const CodeWrap = styled.code`word-break: break-all; font-size: 0.75rem;`;
const JsonPanel = styled.pre`
  margin:0; padding: 0.75rem; border-radius:10px; background:${({theme})=>theme.colors.surfaceMuted}; border:1px solid ${({theme})=>theme.colors.border};
  color:${({theme})=>theme.colors.textPrimary}; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.75rem; overflow:auto; white-space:pre-wrap;
`;
const Notice = styled.p`
  margin: 0.75rem 0 0;
  padding: 0.625rem 0.75rem;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.infoBg};
  color: ${({ theme }) => theme.colors.info};
  font-size: 0.75rem;
`;
